/**
 * lib/api.ts — ตัวกลางเรียก API
 *
 * รายละเอียดที่สำคัญ:
 *   - ใช้ cookie HttpOnly เก็บ token ไม่เก็บใน localStorage ที่ JS อ่านได้
 *     ถ้ามีช่องโหว่ XSS สคริปต์แปลกปลอมจะขโมย token ไปไม่ได้
 *   - access token อายุ 15 นาที เมื่อได้ 401 จะขอ refresh ให้อัตโนมัติหนึ่งครั้ง
 *     แล้วยิงคำขอเดิมซ้ำ ผู้ใช้จึงไม่เจอหน้า login เด้งกลางงาน
 *   - ถ้ามีหลายคำขอเจอ 401 พร้อมกัน จะ refresh แค่ครั้งเดียวแล้วรอผลร่วมกัน
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string = 'ERROR',
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** เรียกเมื่อ session หมดจริงๆ ให้ชั้น auth พาไปหน้า login */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

declare global {
  interface Window {
    __CONFIG__?: {
      API_BASE_URL?: string;
    };
  }
}

/**
 * ดึง base URL ของ API
 * ลำดับการหา:
 * 1. window.__CONFIG__?.API_BASE_URL (runtime config ไม่ต้อง rebuild)
 * 2. import.meta.env.VITE_API_BASE_URL (build-time env)
 * 3. ค่าว่าง '' สำหรับ same-origin / reverse proxy (default)
 */
export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.__CONFIG__?.API_BASE_URL) {
    return window.__CONFIG__.API_BASE_URL.replace(/\/+$/, '');
  }
  const envUrl = (import.meta as any).env?.VITE_API_BASE_URL;
  if (envUrl) {
    return String(envUrl).replace(/\/+$/, '');
  }
  return '';
}

/**
 * แปลง path สั้น (เช่น /api/customers) ให้เป็น URL ตาม API_BASE_URL
 * ถ้า appendAuthToken = true จะต่อ ?token=<jwt> สำหรับเปิดในแท็บใหม่หรือลิงก์ตรง
 */
export function resolveApiUrl(path: string, appendAuthToken = false): string {
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  const baseUrl = getApiBaseUrl();
  let fullUrl = baseUrl ? `${baseUrl}${path}` : path;
  if (appendAuthToken) {
    const token = getStoredAccessToken();
    if (token) {
      const separator = fullUrl.includes('?') ? '&' : '?';
      fullUrl = `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }
  return fullUrl;
}

/**
 * แปลง path ให้เป็น absolute URL (มี http(s):// เสมอ) เช่น สำหรับ redirect URI หรือ popup
 */
export function resolveAbsoluteApiUrl(path: string): string {
  const url = resolveApiUrl(path);
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
}

const ACCESS_TOKEN_KEY = 'ft_access_token';
const REFRESH_TOKEN_KEY = 'ft_refresh_token';

export function getStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredTokens(accessToken?: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return;
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** คำขอ refresh ที่กำลังทำงานอยู่ ใช้ร่วมกันเพื่อไม่ให้ refresh ซ้อน */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    const refreshToken = getStoredRefreshToken();
    refreshInFlight = fetch(resolveApiUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          clearStoredTokens();
          return false;
        }
        try {
          const data = await response.json();
          if (data?.accessToken) {
            setStoredTokens(data.accessToken, data.refreshToken);
          }
        } catch {
          // ignore
        }
        return true;
      })
      .catch(() => {
        clearStoredTokens();
        return false;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** ไม่ต้องพยายาม refresh เมื่อได้ 401 (ใช้กับ endpoint ของ auth เอง) */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
  let code = 'ERROR';
  let details: unknown;

  try {
    const body = (await response.json()) as { error?: string; code?: string; details?: unknown };
    if (body.error) message = body.error;
    if (body.code) code = body.code;
    details = body.details;
  } catch {
    // ตอบกลับไม่ใช่ JSON — ใช้ข้อความตั้งต้น
  }

  return new ApiError(response.status, message, code, details);
}

export async function requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, skipRefresh = false, signal } = options;
  const url = resolveApiUrl(path);
  const token = getStoredAccessToken();

  const send = () =>
    fetch(url, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      credentials: 'include',
      ...(signal ? { signal } : {}),
    });

  let response = await send();

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send();
    } else {
      clearStoredTokens();
      onSessionExpired?.();
      throw await parseError(response);
    }
  }

  if (!response.ok) throw await parseError(response);
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestRaw(path, options);

  // 204 หรือ body ว่าง
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  return JSON.parse(text) as T;
}

export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await requestRaw(path, options);
  return response.text();
}

/**
 * ดาวน์โหลดไฟล์ผ่าน fetch (ส่ง Bearer Token อัตโนมัติ ป้องกันปัญหา cookie ข้ามโดเมน)
 */
export async function downloadFile(path: string, defaultFilename: string): Promise<void> {
  const response = await requestRaw(path);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  // อ่านชื่อไฟล์จาก Content-Disposition header ถ้ามี
  const disposition = response.headers.get('content-disposition');
  let filename = defaultFilename;
  if (disposition) {
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const standardMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (utf8Match?.[1]) {
      try {
        filename = decodeURIComponent(utf8Match[1]);
      } catch {
        filename = utf8Match[1];
      }
    } else if (standardMatch?.[1]) {
      filename = standardMatch[1];
    }
  }

  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  getText: (path: string, signal?: AbortSignal) =>
    requestText(path, signal ? { signal } : {}),
  download: (path: string, defaultFilename: string) =>
    downloadFile(path, defaultFilename),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** ประกอบ query string โดยตัดค่าว่างออก ไม่ให้ส่ง ?search= ที่ไม่มีความหมาย */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}
